import React, { useState, useEffect, useRef } from "react"; // Add useRef import
import { ScreenData } from "../types/ScreenTypes";
import { PrayerTimes } from "../types/PrayerTypes";
import { Playlist, ScheduleType } from "../types/index";
import {
  parseTimeToDate,
  getCurrentTimeInTimezone,
  getCurrentIslamicDate, // ✅ Import the new utility
  getFormattedHijriDate, // ✅ Import for initial state
} from "../utils/timeUtils";
import { partitionPlaylists } from "../utils/playlistPartition";
import { getPrayerLength } from "../utils/prayerTimeUtils";

export interface PrayerState {
  isCountdownActive: boolean;
  countdownPrayer: string | null;
  countdownSeconds: number;
  isMakroohTime: boolean;
  makroohReason: string | null;
  makroohSecondsLeft: number; // Seconds remaining in makrooh period
  isScreenBlank: boolean;
  blankScreenSecondsLeft: number; // Seconds remaining until blank screen ends
  blankScreenTotalSeconds: number; // Total seconds in the active blanking window
  blankScreenPrayer: string | null; // Which prayer is currently blanking (e.g. "Fajr")
  currentIslamicDate:
    | string
    | {
        Day: string;
        MonthName: string;
        Year: string;
        [key: string]: any;
      };

  // New properties for managing content display priority
  shouldPauseContent: boolean; // Main flag to determine if content should be paused
  pauseReason: "countdown" | "salah" | "makrooh" | "critical" | null; // Reason for pausing
  activePriority: number; // Current priority level (lower = higher priority)
  resumeTimestamp: number | null; // When to resume normal operation (Unix timestamp)

  // Critical playlist detection
  hasCriticalPlaylist: boolean; // Whether any critical playlist is active
  activeCriticalPlaylist: Playlist | null; // The active critical playlist
}

/**
 * Custom hook to manage all prayer-related state based on screen configuration
 * Following Azure best practices for React application state management
 */
export function usePrayerStateManager(
  screenData: ScreenData | null,
  prayerTimes: PrayerTimes | null,
  tomorrowPrayerTimes?: PrayerTimes | null,
  playlists?: Playlist[],
  activeScheduledPlaylists?: Playlist[],
  evaluatePlaylists?: () => void, // Add this parameter
) {
  // Add ref to track previous state
  const prayerStateRef = useRef<PrayerState>({
    isCountdownActive: false,
    countdownPrayer: null,
    countdownSeconds: 0,
    isMakroohTime: false,
    makroohReason: null,
    makroohSecondsLeft: 0,
    isScreenBlank: false,
    blankScreenSecondsLeft: 0,
    blankScreenTotalSeconds: 0,
    blankScreenPrayer: null,
    currentIslamicDate: "",
    shouldPauseContent: false,
    pauseReason: null,
    activePriority: 999,
    resumeTimestamp: null,
    hasCriticalPlaylist: false,
    activeCriticalPlaylist: null,
  });

  // Ref to always have latest activeScheduledPlaylists without restarting the interval
  const activeScheduledPlaylistsRef = useRef(activeScheduledPlaylists);
  activeScheduledPlaylistsRef.current = activeScheduledPlaylists;

  const [prayerState, setPrayerState] = useState<PrayerState>({
    isCountdownActive: false,
    countdownPrayer: null,
    countdownSeconds: 0,
    isMakroohTime: false,
    makroohReason: null,
    makroohSecondsLeft: 0,
    isScreenBlank: false,
    blankScreenSecondsLeft: 0,
    blankScreenTotalSeconds: 0,
    blankScreenPrayer: null,
    currentIslamicDate: prayerTimes?.HijriDateComponents
      ? {
          ...prayerTimes.HijriDateComponents,
          Day: prayerTimes.HijriDateComponents.day.toString(),
          MonthName: prayerTimes.HijriDateComponents.monthName || "",
          Year: prayerTimes.HijriDateComponents.year.toString(),
        }
      : prayerTimes
        ? getCurrentIslamicDate(
            prayerTimes,
            tomorrowPrayerTimes || null,
            screenData?.changeIslamicDateAtMaghrib ?? true,
            screenData?.timeZone || "UTC",
          )
        : "",

    // Initialize new properties
    shouldPauseContent: false,
    pauseReason: null,
    activePriority: 999, // Default to lowest priority
    resumeTimestamp: null,

    // Initialize critical playlist properties
    hasCriticalPlaylist: false,
    activeCriticalPlaylist: null,
  });

  // Main effect to manage all prayer-related states
  useEffect(() => {
    if (!screenData || !prayerTimes) return;

    const intervalId = setInterval(() => {
      const now = getCurrentTimeInTimezone(screenData.timeZone);
      const updates: Partial<PrayerState> = {};

      // Initial priority calculation (5-tier system)
      let highestPriority = 999;
      let pauseReason = null;

      // 1. Check for prayer countdown (Priority 10)
      updates.isCountdownActive = false;

      const isFriday = now.getDay() === 5; // 0 is Sunday, 5 is Friday

      if (screenData.salahCountdownReminder > 0) {
        // Check each prayer time for upcoming countdown
        const prayerChecks = [
          { name: screenData.customizedNames.fajr, time: prayerTimes.JamFajr },
        ];

        // Friday countdown should follow Jummah slots instead of Dhuhr.
        if (isFriday) {
          if (prayerTimes.Jummah1 && prayerTimes.Jummah1 !== "--:--") {
            prayerChecks.push({
              name: screenData.customizedNames.jummah,
              time: prayerTimes.Jummah1,
            });
          }

          if (prayerTimes.Jummah2 && prayerTimes.Jummah2 !== "--:--") {
            prayerChecks.push({
              name: `${screenData.customizedNames.jummah} 2`,
              time: prayerTimes.Jummah2,
            });
          }

          if (prayerTimes.Jummah3 && prayerTimes.Jummah3 !== "--:--") {
            prayerChecks.push({
              name: `${screenData.customizedNames.jummah} 3`,
              time: prayerTimes.Jummah3,
            });
          }

          if (prayerTimes.Jummah4 && prayerTimes.Jummah4 !== "--:--") {
            prayerChecks.push({
              name: `${screenData.customizedNames.jummah} 4`,
              time: prayerTimes.Jummah4,
            });
          }

          prayerChecks.push(
            {
              name: screenData.customizedNames.asar,
              time: prayerTimes.JamAsar,
            },
            {
              name: screenData.customizedNames.maghrib,
              time: prayerTimes.JamMaghrib,
            },
            {
              name: screenData.customizedNames.isha,
              time: prayerTimes.JamEsha,
            },
          );
        } else {
          prayerChecks.push(
            {
              name: screenData.customizedNames.dhuhr,
              time: prayerTimes.JamDhuhr,
            },
            {
              name: screenData.customizedNames.asar,
              time: prayerTimes.JamAsar,
            },
            {
              name: screenData.customizedNames.maghrib,
              time: prayerTimes.JamMaghrib,
            },
            {
              name: screenData.customizedNames.isha,
              time: prayerTimes.JamEsha,
            },
          );
        }

        for (const prayer of prayerChecks) {
          const prayerTime = parseTimeToDate(prayer.time);
          if (!prayerTime) continue;

          const diffSeconds = Math.floor(
            (prayerTime.getTime() - now.getTime()) / 1000,
          );

          // If we're within the countdown window
          if (
            diffSeconds >= 0 &&
            diffSeconds <= screenData.salahCountdownReminder
          ) {
            updates.isCountdownActive = true;
            updates.countdownPrayer = prayer.name;
            updates.countdownSeconds = diffSeconds;
            pauseReason = "countdown";
            highestPriority = 10; // Set to countdown priority
            break;
          }
        }
      }

      // 2. Check for screen blanking during prayer (Priority 20)
      updates.isScreenBlank = false;
      updates.blankScreenSecondsLeft = 0;
      updates.blankScreenTotalSeconds = 0;
      updates.blankScreenPrayer = null;

      // Define prayer blanking checks if countdown is not active
      if (!updates.isCountdownActive || highestPriority > 20) {
        const blankingChecks = [
          {
            name: screenData.customizedNames.fajr,
            enabled: screenData.blankScreenDuringFajr,
            jamTime: prayerTimes.JamFajr,
            prayerKey: "JamFajr",
            offset: screenData.fajrBlankStartOffset,
            duration: screenData.fajrBlankDuration,
          },
          // On Fridays, Jummah replaces Dhuhr — exclude Dhuhr to prevent it
          // hijacking the blank screen slot before the Jummah offset fires
          ...(!isFriday
            ? [
                {
                  name: screenData.customizedNames.dhuhr,
                  enabled: screenData.blankScreenDuringDhuhr,
                  jamTime: prayerTimes.JamDhuhr,
                  prayerKey: "JamDhuhr",
                  offset: screenData.dhuhrBlankStartOffset,
                  duration: screenData.dhuhrBlankDuration,
                },
              ]
            : []),
          {
            name: screenData.customizedNames.asar,
            enabled: screenData.blankScreenDuringAsar,
            jamTime: prayerTimes.JamAsar,
            prayerKey: "JamAsar",
            offset: screenData.asarBlankStartOffset,
            duration: screenData.asarBlankDuration,
          },
          {
            name: screenData.customizedNames.maghrib,
            enabled: screenData.blankScreenDuringMaghrib,
            jamTime: prayerTimes.JamMaghrib,
            prayerKey: "JamMaghrib",
            offset: screenData.maghribBlankStartOffset,
            duration: screenData.maghribBlankDuration,
          },
          {
            name: screenData.customizedNames.isha,
            enabled: screenData.blankScreenDuringIsha,
            jamTime: prayerTimes.JamEsha,
            prayerKey: "JamEsha",
            offset: screenData.ishaBlankStartOffset,
            duration: screenData.ishaBlankDuration,
          },
        ];

        // Add Jummah if available and only on Fridays
        if (
          isFriday &&
          prayerTimes.Jummah1 &&
          prayerTimes.Jummah1 !== "--:--"
        ) {
          blankingChecks.push({
            name: screenData.customizedNames.jummah,
            enabled: screenData.blankScreenDuringJummah,
            jamTime: prayerTimes.Jummah1,
            prayerKey: "Jummah1",
            offset: screenData.jummahBlankStartOffset,
            duration: screenData.jummahBlankDuration,
          });
        }

        // Also add additional Jummah prayers for blanking if configured
        if (isFriday && screenData.blankScreenDuringJummah) {
          // Check for Jummah2
          if (prayerTimes.Jummah2 && prayerTimes.Jummah2 !== "--:--") {
            blankingChecks.push({
              name: `${screenData.customizedNames.jummah} 2`,
              enabled: true,
              jamTime: prayerTimes.Jummah2,
              prayerKey: "Jummah2",
              offset: screenData.jummahBlankStartOffset,
              duration: screenData.jummahBlankDuration,
            });
          }

          // Check for Jummah3
          if (prayerTimes.Jummah3 && prayerTimes.Jummah3 !== "--:--") {
            blankingChecks.push({
              name: `${screenData.customizedNames.jummah} 3`,
              enabled: true,
              jamTime: prayerTimes.Jummah3,
              prayerKey: "Jummah3",
              offset: screenData.jummahBlankStartOffset,
              duration: screenData.jummahBlankDuration,
            });
          }

          // Check for Jummah4
          if (prayerTimes.Jummah4 && prayerTimes.Jummah4 !== "--:--") {
            blankingChecks.push({
              name: `${screenData.customizedNames.jummah} 4`,
              enabled: true,
              jamTime: prayerTimes.Jummah4,
              prayerKey: "Jummah4",
              offset: screenData.jummahBlankStartOffset,
              duration: screenData.jummahBlankDuration,
            });
          }
        }

        // Check each prayer for blanking
        for (const check of blankingChecks) {
          if (!check.enabled) continue;

          const jamTime = parseTimeToDate(check.jamTime);
          if (!jamTime) continue;

          // Calculate start and end time for blanking
          const blankStartTime = new Date(
            jamTime.getTime() + check.offset * 60000,
          );
          const prayerLengthSeconds = Math.max(
            0,
            getPrayerLength(check.prayerKey, screenData),
          );
          const blankEndTime = new Date(
            blankStartTime.getTime() + prayerLengthSeconds * 1000,
          );

          if (now >= blankStartTime && now <= blankEndTime) {
            updates.isScreenBlank = true;
            updates.blankScreenSecondsLeft = Math.max(
              0,
              Math.round((blankEndTime.getTime() - now.getTime()) / 1000),
            );
            updates.blankScreenTotalSeconds = prayerLengthSeconds;
            updates.blankScreenPrayer = check.name;
            if (highestPriority > 20) {
              // Only update if higher priority than current
              pauseReason = "salah";
              highestPriority = 20; // Set to salah priority
            }
            break;
          }
        }
      }

      // 3. Check for Makrooh times (Priority 25 - elevated above critical playlists)
      updates.isMakroohTime = false;
      updates.makroohSecondsLeft = 0;

      if (
        !updates.isCountdownActive &&
        !updates.isScreenBlank &&
        screenData.showMakroohWarnings !== false
      ) {
        // Check sunrise makrooh period
        if (screenData.sunriseMakroohPeriod > 0) {
          const sunriseTime = parseTimeToDate(prayerTimes.Sunrise);
          if (sunriseTime) {
            const sunriseMakroohEnd = new Date(
              sunriseTime.getTime() + screenData.sunriseMakroohPeriod * 60000,
            );
            if (now >= sunriseTime && now <= sunriseMakroohEnd) {
              updates.isMakroohTime = true;
              updates.makroohReason = "sunrise";
              updates.makroohSecondsLeft = Math.floor(
                (sunriseMakroohEnd.getTime() - now.getTime()) / 1000,
              );
              if (
                highestPriority > 25 &&
                screenData.pausePlaylistsDuringMakrooh !== false
              ) {
                pauseReason = "makrooh";
                highestPriority = 25; // Makrooh priority (elevated to position 3)
              }
            }
          }
        }

        // Check zawwal makrooh period
        if (!updates.isMakroohTime && screenData.zawwalMakroohPeriod > 0) {
          const begDhuhrTime = parseTimeToDate(prayerTimes.BegDhuhr);
          if (begDhuhrTime) {
            // Makrooh period: from (begDhuhr - duration) to begDhuhr
            const zawwalMakroohStart = new Date(
              begDhuhrTime.getTime() - screenData.zawwalMakroohPeriod * 60000,
            );
            const zawwalMakroohEnd = begDhuhrTime;

            if (now >= zawwalMakroohStart && now <= zawwalMakroohEnd) {
              updates.isMakroohTime = true;
              updates.makroohReason = "zawwal";
              updates.makroohSecondsLeft = Math.floor(
                (zawwalMakroohEnd.getTime() - now.getTime()) / 1000,
              );
              if (
                highestPriority > 25 &&
                screenData.pausePlaylistsDuringMakrooh !== false
              ) {
                pauseReason = "makrooh";
                highestPriority = 25; // Makrooh priority (elevated to position 3)
              }
            }
          }
        }

        // Check sunset makrooh period (starts X mins before sunset/begMaghrib/jamMaghrib, ends at that time)
        if (!updates.isMakroohTime && screenData.sunsetMakroohPeriod > 0) {
          // Try sunset, then begMaghrib, then jamMaghrib
          let referenceTime =
            parseTimeToDate(prayerTimes.Sunset) ||
            parseTimeToDate(prayerTimes.BegMaghrib) ||
            parseTimeToDate(prayerTimes.JamMaghrib);

          if (referenceTime) {
            const sunsetMakroohStart = new Date(
              referenceTime.getTime() - screenData.sunsetMakroohPeriod * 60000,
            );
            const sunsetMakroohEnd = referenceTime;

            if (now >= sunsetMakroohStart && now <= sunsetMakroohEnd) {
              updates.isMakroohTime = true;
              updates.makroohReason = "sunset";
              updates.makroohSecondsLeft = Math.floor(
                (sunsetMakroohEnd.getTime() - now.getTime()) / 1000,
              );
              if (
                highestPriority > 25 &&
                screenData.pausePlaylistsDuringMakrooh !== false
              ) {
                pauseReason = "makrooh";
                highestPriority = 25; // Makrooh priority (elevated to position 3)
              }
            }
          }
        }
      }

      // 4. Check for critical playlists (Priority 40)
      updates.hasCriticalPlaylist = false;
      updates.activeCriticalPlaylist = null;

      if (
        !updates.isCountdownActive &&
        !updates.isScreenBlank &&
        !updates.isMakroohTime &&
        activeScheduledPlaylistsRef.current &&
        activeScheduledPlaylistsRef.current.length > 0 &&
        highestPriority > 40
      ) {
        // Use the partitioning utility to detect critical playlists from ACTIVE scheduled playlists
        const partitioned = partitionPlaylists(
          activeScheduledPlaylistsRef.current,
        );

        if (partitioned.critical) {
          updates.hasCriticalPlaylist = true;
          updates.activeCriticalPlaylist = partitioned.critical;
          pauseReason = "critical";
          highestPriority = 40; // Critical playlist priority (position 4)
        } else {
          // Fallback: Any active PrayerBased playlist should be treated as critical
          // (e.g., "after salah" playlists with priority < 5 that the scheduler activated),
          // unless the playlist has opted to stay contained within its layout.
          const prayerBasedPlaylist = activeScheduledPlaylistsRef.current.find(
            (p) => {
              const st =
                typeof p.scheduleType === "string"
                  ? ScheduleType[p.scheduleType as keyof typeof ScheduleType]
                  : p.scheduleType;
              if (st !== ScheduleType.PrayerBased) return false;
              return p.prayerDisplayMode !== "RespectLayout";
            },
          );
          if (prayerBasedPlaylist) {
            const previousCriticalId =
              prayerStateRef.current.activeCriticalPlaylist?.id;
            if (
              import.meta.env.DEV &&
              previousCriticalId !== prayerBasedPlaylist.id
            ) {
              console.log(
                `🚨 [PrayerStateManager] PrayerBased playlist "${prayerBasedPlaylist.name}" (priority ${prayerBasedPlaylist.priority}) treated as critical`,
              );
            }
            updates.hasCriticalPlaylist = true;
            updates.activeCriticalPlaylist = prayerBasedPlaylist;
            pauseReason = "critical";
            highestPriority = 40;
          }
        }
      } // 5. Implement Islamic date change at Maghrib
      if (screenData.changeIslamicDateAtMaghrib) {
        const maghribTime = parseTimeToDate(prayerTimes.JamMaghrib);
        const midnight = new Date(now);
        midnight.setHours(0, 0, 0, 0);
        const nextMidnight = new Date(midnight);
        nextMidnight.setDate(nextMidnight.getDate() + 1);

        if (
          maghribTime &&
          now >= maghribTime &&
          now < nextMidnight &&
          typeof tomorrowPrayerTimes !== "undefined" &&
          tomorrowPrayerTimes &&
          tomorrowPrayerTimes.HijriDateComponents
        ) {
          // Use components for tomorrow's date with (+1) indicator
          updates.currentIslamicDate = {
            ...tomorrowPrayerTimes.HijriDateComponents,
            Day: tomorrowPrayerTimes.HijriDateComponents.day.toString(),
            MonthName: tomorrowPrayerTimes.HijriDateComponents.monthName || "",
            Year: tomorrowPrayerTimes.HijriDateComponents.year.toString(),
          };
        } else if (prayerTimes.HijriDateComponents) {
          // Use components for today's date
          updates.currentIslamicDate = {
            ...prayerTimes.HijriDateComponents,
            Day: prayerTimes.HijriDateComponents.day.toString(),
            MonthName: prayerTimes.HijriDateComponents.monthName || "",
            Year: prayerTimes.HijriDateComponents.year.toString(),
          };
        } else {
          // Fallback to formatted string if components not available
          updates.currentIslamicDate = getFormattedHijriDate(
            prayerTimes.HijriDate,
          );
        }
      }

      // Calculate when to resume normal operation
      let resumeTimestamp = null;
      if (pauseReason === "countdown" && updates.countdownSeconds) {
        resumeTimestamp = now.getTime() + updates.countdownSeconds * 1000;
      } else if (pauseReason === "salah" && updates.isScreenBlank) {
        // Find the active blanking check to determine end time
        const activeBlankingCheck = [
          {
            enabled: screenData.blankScreenDuringFajr,
            jamTime: prayerTimes.JamFajr,
            prayerKey: "JamFajr",
            offset: screenData.fajrBlankStartOffset,
            duration: screenData.fajrBlankDuration,
          },
          {
            enabled: screenData.blankScreenDuringDhuhr,
            jamTime: prayerTimes.JamDhuhr,
            prayerKey: "JamDhuhr",
            offset: screenData.dhuhrBlankStartOffset,
            duration: screenData.dhuhrBlankDuration,
          },
          {
            enabled: screenData.blankScreenDuringAsar,
            jamTime: prayerTimes.JamAsar,
            prayerKey: "JamAsar",
            offset: screenData.asarBlankStartOffset,
            duration: screenData.asarBlankDuration,
          },
          {
            enabled: screenData.blankScreenDuringMaghrib,
            jamTime: prayerTimes.JamMaghrib,
            prayerKey: "JamMaghrib",
            offset: screenData.maghribBlankStartOffset,
            duration: screenData.maghribBlankDuration,
          },
          {
            enabled: screenData.blankScreenDuringIsha,
            jamTime: prayerTimes.JamEsha,
            prayerKey: "JamEsha",
            offset: screenData.ishaBlankStartOffset,
            duration: screenData.ishaBlankDuration,
          },
          // Include Jummah if available
          {
            enabled: screenData.blankScreenDuringJummah,
            jamTime: prayerTimes.Jummah1,
            prayerKey: "Jummah1",
            offset: screenData.jummahBlankStartOffset,
            duration: screenData.jummahBlankDuration,
          },
        ].find((check) => {
          if (!check.enabled) return false;
          const jamTime = parseTimeToDate(check.jamTime);
          if (!jamTime) return false;

          const blankStartTime = new Date(
            jamTime.getTime() + check.offset * 60000,
          );
          const prayerLengthSeconds = Math.max(
            0,
            getPrayerLength(check.prayerKey, screenData),
          );
          const blankEndTime = new Date(
            blankStartTime.getTime() + prayerLengthSeconds * 1000,
          );

          return now >= blankStartTime && now <= blankEndTime;
        });

        if (activeBlankingCheck) {
          const jamTime = parseTimeToDate(activeBlankingCheck.jamTime);
          if (jamTime) {
            const blankEndTime = new Date(
              jamTime.getTime() +
                activeBlankingCheck.offset * 60000 +
                Math.max(
                  0,
                  getPrayerLength(activeBlankingCheck.prayerKey, screenData),
                ) *
                  1000,
            );
            resumeTimestamp = blankEndTime.getTime();
          }
        }
      } else if (pauseReason === "makrooh" && updates.isMakroohTime) {
        // For makrooh, we use a fixed duration if configured
        if (screenData.makroohWarningDuration) {
          resumeTimestamp =
            now.getTime() + screenData.makroohWarningDuration * 1000;
        }
      } else if (pauseReason === "critical" && updates.hasCriticalPlaylist) {
        // For critical playlists, resume is handled by the enhanced scheduler
        // This indicates when the critical playlist schedule period ends
        // The actual duration depends on the playlist's schedule configuration
        resumeTimestamp = null; // Let the scheduler determine when to end
      }

      // Final updates to state
      updates.shouldPauseContent = highestPriority < 100; // Below 100 means we should pause
      updates.pauseReason = pauseReason as any;
      updates.activePriority = highestPriority;
      updates.resumeTimestamp = resumeTimestamp;

      // Log critical playlist state transitions for debugging
      if (
        updates.hasCriticalPlaylist !==
        prayerStateRef.current.hasCriticalPlaylist
      ) {
        console.log(
          `🔄 [PrayerStateManager] Critical playlist state: ${prayerStateRef.current.hasCriticalPlaylist} → ${updates.hasCriticalPlaylist}`,
          updates.hasCriticalPlaylist
            ? `playlist: "${updates.activeCriticalPlaylist?.name}"`
            : "(cleared)",
        );
      }

      // Update state with all changes
      setPrayerState((prevState) => ({
        ...prevState,
        ...updates,
      }));

      // Invoke the evaluation callback if provided
      if (evaluatePlaylists) {
        evaluatePlaylists();
      }

      // Update the ref to the latest state
      prayerStateRef.current = {
        ...prayerStateRef.current,
        ...updates,
      };
    }, 1000);

    return () => clearInterval(intervalId);
  }, [
    screenData,
    prayerTimes,
    tomorrowPrayerTimes,
    playlists,
    // activeScheduledPlaylists accessed via ref — no need to restart interval
  ]);

  // Add this effect after your main state management effect
  useEffect(() => {
    // Skip if evaluatePlaylists callback is not provided
    if (!evaluatePlaylists) return;

    // Check if a prayer has just ended (was blank, now not blank)
    if (prayerStateRef.current.isScreenBlank && !prayerState.isScreenBlank) {
      console.log(
        "🔄 [PrayerStateManager] Prayer just ended, triggering immediate playlist evaluation",
      );
      evaluatePlaylists(); // Force immediate evaluation
    }

    // Update ref for next comparison
    prayerStateRef.current = {
      ...prayerStateRef.current,
      isScreenBlank: prayerState.isScreenBlank,
    };
  }, [prayerState.isScreenBlank, evaluatePlaylists]);

  return prayerState;
}
